// 📁 src/pages/FundingMatch.jsx

import React, { useState, useEffect, useRef } from "react";
import axios from "../utils/axiosInstance";
import IdeaPopup from "./IdeaPopup";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate, useLocation } from "react-router-dom";

import ModuleHeader from "../components/ModuleHeader";
import { useIdea } from "../context/IdeaContext";

/* ------------------------------------------------------------------
   Dropdown Options
------------------------------------------------------------------ */

const STAGES=[
"Idea",
"Pre-Seed",
"Seed",
"Seed+",
"Series A",
"Series B+",
"Grant / Non-dilutive"
];

const SECTORS=[
"Technology",
"AI / ML",
"FinTech",
"AgriTech",
"HealthTech",
"EdTech",
"ClimateTech",
"SaaS",
"DeepTech",
"Consumer",
"Enterprise",
"Other"
];

const COUNTRIES=[
"India",
"United States",
"United Kingdom",
"Singapore",
"UAE",
"Australia",
"Canada",
"Germany",
"Other"
];

const FundingMatch=()=>{

const navigate=useNavigate();
const location=useLocation();

const {selectedIdea,businessModel}=useIdea();

/* ================= STARTUP RESOLVER ================= */

const startupName=

selectedIdea?.ideaTitle ||

selectedIdea?.title ||

businessModel?.startupName ||

localStorage.getItem("activeIdeaTitle") ||

"Startup Idea";


const ideaId=
location.state?.ideaId ||
selectedIdea?.ideaId ||
null;

const fundingAskFromDeck=
location.state?.fundingAsk || "";

const tractionFromDeck=
location.state?.tractionHighlights || "";

const keywordsFromDeck=
location.state?.investorKeywords || [];


/* ================= STATE ================= */

const[startupDetails,setStartupDetails]=useState({

stage:"Seed",
sector:"Technology",
region:"India"

});

const[dbInvestors,setDbInvestors]=useState([]);
const[aiInvestors,setAiInvestors]=useState([]);
const[govtSchemes,setGovtSchemes]=useState([]);

const[loading,setLoading]=useState(false);
const[prefilling,setPrefilling]=useState(true);
const[userEdited,setUserEdited]=useState(false);

const autoSaveTimer=useRef(null);

const[showPopup,setShowPopup]=useState(false);
const[popupPayload,setPopupPayload]=useState(null);


/* ================= AUTO SAVE ================= */

const autoSaveFundingInputs=(newDetails)=>{

if(autoSaveTimer.current)
clearTimeout(autoSaveTimer.current);

autoSaveTimer.current=setTimeout(async()=>{

try{

await axios.post("/api/funding-match/save",{

ideaId,
startupDetails:newDetails

});

setPopupPayload({

idea:{title:"Funding Match"},

type:"progress",

aiMessageOverride:
"Your funding preferences are saved."

});

setShowPopup(true);

setTimeout(()=>setShowPopup(false),2500);

}catch{}

},900);

};


/* ================= INPUT CHANGE ================= */

const handleSelectChange=(e)=>{

const updated={
...startupDetails,
[e.target.name]:e.target.value
};

setStartupDetails(updated);

setUserEdited(true);

autoSaveFundingInputs(updated);

};


/* ================= PREFILL ================= */

useEffect(()=>{

setPrefilling(true);

setStartupDetails({

stage:"Seed",
sector:"Technology",
region:"India"

});

setPrefilling(false);

},[]);


/* ================= FETCH ================= */

const fetchFundingMatches=async()=>{

setLoading(true);

try{

const res = await axios.post("/api/funding-match/match", {
  ...startupDetails,
  ideaId,
  solutionText: selectedIdea?.description || selectedIdea?.problemStatement || "",
  fundingAsk: fundingAskFromDeck,
  traction: tractionFromDeck,
  keywords: keywordsFromDeck,
  userModified: userEdited
});

setDbInvestors(res.data.dbInvestors||[]);
setAiInvestors(res.data.aiInvestors||[]);
setGovtSchemes(res.data.govtSchemes||[]);

if (
  (res.data.dbInvestors || []).length === 0 &&
  (res.data.aiInvestors || []).length === 0 &&
  (res.data.govtSchemes || []).length === 0
) {
  setPopupPayload({
    idea: { title: "No Matches Found" },
    type: "feedback",
    aiMessageOverride:
      "Try adjusting stage, sector, or region to improve matches."
  });

  setShowPopup(true);
  setTimeout(() => setShowPopup(false), 3000);
}

setPopupPayload({
  idea:{title:"Funding Matches Ready"},
  type:"success",
  aiMessageOverride:
    "I’ve shortlisted funding options aligned with your startup profile."
});

setShowPopup(true);

setTimeout(()=>setShowPopup(false),3000);

}catch{}

finally{

setLoading(false);

}

};


/* ================= PDF ================= */

const downloadPDF=()=>{

const doc=new jsPDF();

doc.text("Funding Match Results",14,12);

const rows=[

...dbInvestors,
...aiInvestors,
...govtSchemes

].map(m=>[

m.name,

m.stage||"-",

m.sector||"-",

m.ticketSize||m.support||"-",

m.region||"-",

`${m.matchScore||"-"}%`

]);

doc.autoTable({

head:[["Name","Stage","Sector","Support","Region","Match"]],

body:rows,

startY:18

});

doc.save("Funding_Match.pdf");

};


/* ================= CARD ================= */

const FundingCard=({title,items,actionLabel})=>(

<div className="space-y-4">

<h3 className="text-xl font-semibold text-blue-900">

{title}

</h3>

{items.length===0 &&(

<p className="text-slate-500 text-sm">

No matches found.

</p>

)}

<div className="grid md:grid-cols-2 gap-4">

{items.map((m,i)=>(

<div

key={i}

className="border rounded-xl p-4 bg-white hover:shadow-sm transition"

>

<div className="font-semibold text-slate-800">

{m.name}

</div>

<div className="text-sm text-slate-600 mt-2 space-y-1">

<div>Stage: {m.stage||"-"}</div>

<div>Sector: {m.sector||"-"}</div>

<div>
Support: {m.ticketSize||m.support||m.benefit||"-"}
</div>

<div>Region: {m.region||"-"}</div>

</div>

{m.matchScore &&(

<div className="mt-2 text-green-700 text-sm font-semibold">

Match Score: {m.matchScore}%

</div>

)}

<a

href={m.website||"#"}

target="_blank"

rel="noopener noreferrer"

className="inline-block mt-3 text-blue-600 font-semibold text-sm"

>

{actionLabel} →

</a>

</div>

))}

</div>

</div>

);


/* ================= UI ================= */

return(

<div className="bg-slate-50 min-h-screen">

<div className="max-w-6xl mx-auto px-6 py-10">

{showPopup && popupPayload &&(

<div className="fixed bottom-5 right-5 z-50">

<IdeaPopup {...popupPayload} autoClose />

</div>

)}


<ModuleHeader

title="Funding Match"

subtitle="Discover investors and funding programs aligned with your startup."

currentModule="funding"

startup={startupName}

badges={[

"Investor Discovery",

"AI Matching",

"Funding Ready"

]}

/>


{/* ACTIVE IDEA */}

<div className="text-sm text-gray-500 border-b border-slate-200 pb-4 mb-6">

<b>Startup:</b> {startupName}

</div>


{/* INPUTS */}

{!prefilling &&(

<div className="grid md:grid-cols-3 gap-4 mb-6">

<select

name="stage"

value={startupDetails.stage}

onChange={handleSelectChange}

className="border rounded-lg p-3 bg-white"

>

{STAGES.map(s=>(

<option key={s}>{s}</option>

))}

</select>

<select

name="sector"

value={startupDetails.sector}

onChange={handleSelectChange}

className="border rounded-lg p-3 bg-white"

>

{SECTORS.map(s=>(

<option key={s}>{s}</option>

))}

</select>

<select

name="region"

value={startupDetails.region}

onChange={handleSelectChange}

className="border rounded-lg p-3 bg-white"

>

{COUNTRIES.map(c=>(

<option key={c}>{c}</option>

))}

</select>

</div>

)}


<button

onClick={fetchFundingMatches}

disabled={loading}

className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700"

>

{loading?"Analyzing…":"Match Me with Funders"}

</button>


{(dbInvestors.length>0 ||

aiInvestors.length>0 ||

govtSchemes.length>0)&&(

<div className="mt-12 space-y-12">

<FundingCard

title="Database Investors"

items={dbInvestors}

actionLabel="Apply"

/>

<FundingCard

title="AI-Suggested Investors"

items={aiInvestors}

actionLabel="Explore"

/>

<FundingCard

title="Government Schemes"

items={govtSchemes}

actionLabel="Learn More"

/>


<div className="mt-12 border rounded-2xl p-8 bg-gradient-to-r from-blue-50 to-purple-50">

<h3 className="text-2xl font-semibold text-blue-900">

🚀 Mentor Network Launching Soon

</h3>

<p className="text-slate-600 mt-3 max-w-2xl">

You are among the early founders building on this platform.

We are carefully onboarding experienced founders, investors and operators to guide startups like yours.

</p>

<div className="mt-4 text-sm text-slate-600">

Continue refining your startup journey and you will receive:

</div>

<div className="mt-3 text-sm text-slate-500">

✨ Priority mentor access  
✨ Early investor introduction opportunities  
✨ Founder-only strategy sessions  
✨ Private startup community  

</div>

<button

onClick={()=>{

setPopupPayload({

idea:{title:"Mentor Network"},

type:"progress",

aiMessageOverride:
"You are on the early founder list. Continue building and you will get priority mentor access."

});

setShowPopup(true);

setTimeout(()=>setShowPopup(false),3200);

}}

className="mt-6 bg-purple-600 text-white px-7 py-3 rounded-lg font-semibold hover:bg-purple-700"

>

Get Early Founder Access →

</button>

</div>


<div className="flex flex-wrap gap-4 pt-6 border-t">



<button

onClick={()=>navigate("/gtm-accelerator")}

className="bg-purple-600 text-white px-6 py-2.5 rounded-lg"

>

Back to GTM →

</button>

</div>

</div>

)}

</div>

</div>

);

};

export default FundingMatch;